import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsControlActionType,
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicObject,
  HsMatrix4,
  HsQuaternion,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateConeGeometryData,
  hsGenerateCubeGeometryData,
  hsGenerateCylinderGeometryData,
  hsGeneratePlaneGeometryData,
  hsGenerateSphereGeometryData,
  hsGenerateTorusGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  hsMergeGeometries,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsHypersceneViewerOptions,
  type HsMaterial,
  type HsRegularGeometryData,
  type HsViewerInputOptions,
  type HsViewerPick,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { RenderType } from '../../enums/render-type.enum';
import { makeLitMaterial } from '../../functions/make-lit-material.function';
import { RenderModesHandler } from './classes/render-modes-handler.class';
import {
  GizmoAction,
  TransformGizmo,
  makeAxisRotation,
  makePointerRay,
  type GizmoHandle,
} from './classes/transform-gizmo.class';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [2, 10, 30];
const GROUND_SIZE: number = 40;
const AXIS_COLORS: HsHypersceneTypes.Array3[] = [
  [0.9, 0.1, 0.1],
  [0, 0.7, 0],
  [0.3, 0.3, 1],
];
/** Plane handle colors by plane normal: YZ, XZ, XY. */
const PLANE_COLORS: HsHypersceneTypes.Array3[] = [
  [0.1, 0.9, 0.9],
  [0.9, 0.1, 0.9],
  [0.9, 0.9, 0.1],
];
const HIGHLIGHT_COLOR: HsHypersceneTypes.Array3 = [1, 0.85, 0.3];
const ARROW_LENGTH: number = 4.5;
const PLANE_HANDLE_OFFSET: number = 2.8;
const PLANE_HANDLE_SIZE: number = 0.6;
const RING_RADIUS: number = 3.6;

function makeMatrix(
  position: HsHypersceneTypes.Array3,
  rotation: HsQuaternion = HsQuaternion.makeIdentityQuaternion(),
  scale: HsHypersceneTypes.Array3 = [1, 1, 1]
): HsMatrix4 {
  return HsMatrix4.compose(position, rotation.values, scale);
}

function makeTransparentMaterial(
  gl: WebGL2RenderingContext,
  color: HsHypersceneTypes.Array3,
  alpha: number
): HsMaterial {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth().addNormals().addDiffuseLighting().addTransparency(alpha);
  const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
  material.color.set(...color);
  return material;
}

/** A shaft along +Y from the origin to `length` ending in a cone (move) or a cube (scale). */
function makeHandleGeometry(graphics: HsGraphics, length: number, scaleTip: boolean): HsGeometry | null {
  const shaftLength: number = length * (scaleTip ? 0.9 : 0.8);
  const shaft: [HsRegularGeometryData, HsMatrix4] = [
    hsGenerateCylinderGeometryData(12),
    makeMatrix([0, shaftLength / 2, 0], undefined, [0.06, shaftLength / 2, 0.06]),
  ];
  const tip: [HsRegularGeometryData, HsMatrix4] = scaleTip
    ? [hsGenerateCubeGeometryData(), makeMatrix([0, length, 0], undefined, [0.2, 0.2, 0.2])]
    : [hsGenerateConeGeometryData(12), makeMatrix([0, shaftLength, 0], undefined, [0.2, length - shaftLength, 0.2])];
  return hsMakeIndexedGeometry(graphics, hsIndexGeometry(hsMergeGeometries([shaft, tip])));
}

/**
 * Interactive tools: drag the arrows and squares to move the white cube, the rings to rotate the blue one and
 * the cube-tipped handles to scale the orange one. The left mouse button drives the tools; the middle and right
 * buttons, the wheel and the keyboard move the camera. The footer names the work each frame did.
 */
@Component({
  selector: 'hyperscene-ui-tools',
  templateUrl: './ui-tools.component.html',
  styleUrls: ['./ui-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class UiToolsDemo extends DemoViewerPage {
  public readonly renderActionsTypes: Map<number, string> = new Map([
    [RenderType.None, RenderType[RenderType.None]],
    [RenderType.Transform, RenderType[RenderType.Transform]],
    [RenderType.UpdateItems, RenderType[RenderType.UpdateItems]],
    [RenderType.UpdateRaycast, RenderType[RenderType.UpdateRaycast]],
    [RenderType.UpdateSelect, RenderType[RenderType.UpdateSelect]],
    [RenderType.UpdateOver, RenderType[RenderType.UpdateOver]],
  ]);

  public readonly renderActions: Map<number, boolean> = new Map([
    [RenderType.None, false],
    [RenderType.Transform, false],
    [RenderType.UpdateItems, false],
    [RenderType.UpdateRaycast, false],
    [RenderType.UpdateSelect, false],
    [RenderType.UpdateOver, false],
  ]);

  private readonly gizmos: TransformGizmo[] = [];
  private readonly renderModesHandler: RenderModesHandler = new RenderModesHandler();
  private readonly lastCameraMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private readonly listeners: (() => void)[] = [];
  private renderType: RenderType = RenderType.None;
  private pointer: HsHypersceneTypes.Array2 | null = null;
  private pointerMoved: boolean = false;
  private cameraMoved: boolean = false;
  private objectsMoved: boolean = false;
  private dragged: TransformGizmo | null = null;
  private ground: HsGraphicCollection | null = null;
  private hovered: HsGraphicObject | null = null;
  private hoveredMaterial: HsMaterial | null = null;
  private highlightMaterial: HsMaterial | null = null;

  protected override viewerOptions(): Partial<HsHypersceneViewerOptions> {
    return { clearColor: [0.85, 0.92, 1, 1] };
  }

  protected override inputOptions(): HsViewerInputOptions {
    // The left button drives the tools; the middle and right buttons pan and orbit.
    return { dragActions: [null, HsControlActionType.MiddleMove, HsControlActionType.RightMove] };
  }

  protected initScene(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const cubeGeometry: HsGeometry | null = hsMakeGeometry(graphics, cubeData);
    const indexedCubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(cubeData));
    if (cubeGeometry === null || indexedCubeGeometry === null) {
      return;
    }
    this.highlightMaterial = makeLitMaterial(viewer.gl, ...HIGHLIGHT_COLOR);

    const movedCube: HsGraphicObject = this.addObject(
      viewer,
      'cube',
      cubeGeometry,
      makeLitMaterial(viewer.gl, 1, 1, 1)
    );
    movedCube.worldMatrix.copy(makeMatrix([5, 3, 10], HsQuaternion.makeFromAxisAndAngle([0, 1, 0], 1), [2, 2, 2]));
    const scaledCube: HsGraphicObject = this.addObject(
      viewer,
      'scaled cube',
      indexedCubeGeometry,
      makeLitMaterial(viewer.gl, 1, 0.75, 0)
    );
    scaledCube.worldMatrix.copy(makeMatrix([-15, 2, 0], undefined, [2, 2, 2]));
    const rotatedCube: HsGraphicObject = this.addObject(
      viewer,
      'rotated cube',
      indexedCubeGeometry,
      makeLitMaterial(viewer.gl, 0.5, 0.75, 1)
    );
    rotatedCube.worldMatrix.copy(makeMatrix([-5, 2, 10], undefined, [2, 2, 2]));
    [movedCube, scaledCube, rotatedCube].forEach((object: HsGraphicObject) => object.updateBoundingSphere());
    this.addGround(viewer, indexedCubeGeometry);

    this.addMoveGizmo(viewer, movedCube);
    this.addRotateGizmo(viewer, rotatedCube);
    this.addScaleGizmo(viewer, scaledCube);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
    viewer.camera.updateProjectionMatrix();
    viewer.onFrame(() => this.afterFrame(viewer));
    const canvas: HTMLCanvasElement = viewer.surface.canvas;
    this.listen(canvas, 'mousedown', (event: MouseEvent) => this.startDrag(viewer, event));
    // Drags keep going when the pointer leaves the canvas.
    this.listen(canvas.ownerDocument, 'mousemove', (event: MouseEvent) => {
      this.pointer = [event.clientX, event.clientY];
      this.pointerMoved = true;
    });
    this.listen(canvas.ownerDocument, 'mouseup', () => this.endDrag());
  }

  /** Applies a tool drag before culling, so the moved objects are culled and drawn in the same frame. */
  protected override animate(viewer: HsHypersceneViewer): boolean {
    this.cameraMoved = !viewer.camera.worldMatrix.equals(this.lastCameraMatrix, 0.001);
    this.lastCameraMatrix.copy(viewer.camera.worldMatrix);
    this.objectsMoved = false;
    if (this.dragged !== null && this.pointerMoved && this.pointer !== null) {
      this.pointerMoved = false;
      this.objectsMoved = this.dragged.drag(makePointerRay(viewer, this.pointer[0], this.pointer[1]));
    }
    return this.objectsMoved;
  }

  public override ngOnDestroy(): void {
    this.listeners.forEach((remove: () => void) => remove());
    super.ngOnDestroy();
  }

  private addObject(
    viewer: HsHypersceneViewer,
    name: string,
    geometry: HsGeometry,
    material: HsMaterial
  ): HsGraphicObject {
    const object: HsGraphicObject = new HsGraphicObject(getUuid(), name, geometry, material);
    viewer.add(object);
    return object;
  }

  private addGround(viewer: HsHypersceneViewer, geometry: HsGeometry): void {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
    const material: HsMaterial = new HsDefaultMaterial(viewer.gl, shaderData.makeShader());
    const ground: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
    for (let i: number = 0; i < GROUND_SIZE; i++) {
      for (let j: number = 0; j < GROUND_SIZE; j++) {
        const shade: number = (i + j) % 2 === 1 ? 0.3 : 1;
        ground.addInstance(makeMatrix([i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE]).values, [shade, shade, shade]);
      }
    }
    ground.updateCenter();
    ground.updateBoundingSphere();
    ground.updateBuffers(viewer.surface.graphics);
    viewer.add(ground);
    this.ground = ground;
  }

  private addMoveGizmo(viewer: HsHypersceneViewer, target: HsGraphicObject): void {
    const gizmo: TransformGizmo = new TransformGizmo(target, [5, 3, 10]);
    const arrowGeometry: HsGeometry | null = makeHandleGeometry(viewer.surface.graphics, ARROW_LENGTH, false);
    const planeGeometry: HsGeometry | null = hsMakeGeometry(viewer.surface.graphics, hsGeneratePlaneGeometryData());
    for (let axis: number = 0; axis < 3; axis++) {
      if (arrowGeometry !== null) {
        const arrow: HsGraphicObject = this.addObject(
          viewer,
          `move arrow ${axis}`,
          arrowGeometry,
          makeLitMaterial(viewer.gl, ...AXIS_COLORS[axis])
        );
        arrow.worldMatrix.copy(makeMatrix([0, 0, 0], makeAxisRotation(axis)));
        gizmo.addHandle(arrow, GizmoAction.Move, axis);
      }
      if (planeGeometry !== null) {
        // The square lies across the two axes other than its normal, off the center so it clears the cube.
        const position: HsHypersceneTypes.Array3 = [PLANE_HANDLE_OFFSET, PLANE_HANDLE_OFFSET, PLANE_HANDLE_OFFSET];
        position[axis] = 0;
        const plane: HsGraphicObject = this.addObject(
          viewer,
          `move plane ${axis}`,
          planeGeometry,
          makeTransparentMaterial(viewer.gl, PLANE_COLORS[axis], 0.5)
        );
        const size: number = PLANE_HANDLE_SIZE;
        plane.worldMatrix.copy(makeMatrix(position, makeAxisRotation(axis), [size, size, size]));
        gizmo.addHandle(plane, GizmoAction.Move, axis, true);
      }
    }
    this.gizmos.push(gizmo);
  }

  private addRotateGizmo(viewer: HsHypersceneViewer, target: HsGraphicObject): void {
    const gizmo: TransformGizmo = new TransformGizmo(target, [-5, 2, 10]);
    const graphics: HsGraphics = viewer.surface.graphics;
    const ringGeometry: HsGeometry | null = hsMakeIndexedGeometry(
      graphics,
      hsGenerateTorusGeometryData(RING_RADIUS, 0.1, 8, 48)
    );
    if (ringGeometry !== null) {
      for (let axis: number = 0; axis < 3; axis++) {
        const ring: HsGraphicObject = this.addObject(
          viewer,
          `rotation ring ${axis}`,
          ringGeometry,
          makeLitMaterial(viewer.gl, ...AXIS_COLORS[axis])
        );
        ring.worldMatrix.copy(makeMatrix([0, 0, 0], makeAxisRotation(axis)));
        gizmo.addHandle(ring, GizmoAction.Rotate, axis);
      }
    }
    const sphereGeometry: HsGeometry | null = hsMakeGeometry(graphics, hsGenerateSphereGeometryData(16));
    if (sphereGeometry !== null) {
      const sphere: HsGraphicObject = this.addObject(
        viewer,
        'rotation sphere',
        sphereGeometry,
        makeTransparentMaterial(viewer.gl, [0.8, 0.8, 0.8], 0.25)
      );
      const radius: number = RING_RADIUS - 0.3;
      sphere.worldMatrix.copy(makeMatrix([0, 0, 0], undefined, [radius, radius, radius]));
      gizmo.addHandle(sphere, GizmoAction.None, 0);
    }
    this.gizmos.push(gizmo);
  }

  private addScaleGizmo(viewer: HsHypersceneViewer, target: HsGraphicObject): void {
    const gizmo: TransformGizmo = new TransformGizmo(target, [-15, 2, 0]);
    const handleGeometry: HsGeometry | null = makeHandleGeometry(viewer.surface.graphics, ARROW_LENGTH - 0.5, true);
    if (handleGeometry !== null) {
      for (let axis: number = 0; axis < 3; axis++) {
        const handle: HsGraphicObject = this.addObject(
          viewer,
          `scale handle ${axis}`,
          handleGeometry,
          makeLitMaterial(viewer.gl, ...AXIS_COLORS[axis])
        );
        handle.worldMatrix.copy(makeMatrix([0, 0, 0], makeAxisRotation(axis)));
        gizmo.addHandle(handle, GizmoAction.Scale, axis);
      }
    }
    this.gizmos.push(gizmo);
  }

  private startDrag(viewer: HsHypersceneViewer, event: MouseEvent): void {
    if (event.button !== 0) {
      return;
    }
    const pick: HsViewerPick | null = viewer.pick(event.clientX, event.clientY);
    if (pick === null) {
      return;
    }
    for (const gizmo of this.gizmos) {
      const handle: GizmoHandle | null = gizmo.findHandle(pick.object);
      if (handle !== null && gizmo.beginDrag(handle, makePointerRay(viewer, event.clientX, event.clientY))) {
        this.dragged = gizmo;
        return;
      }
    }
  }

  private endDrag(): void {
    this.dragged?.endDrag();
    this.dragged = null;
    // Pick again: the pointer may have left the handle during the drag.
    this.pointerMoved = true;
  }

  /** Picks after the frame was drawn, highlights the object under the pointer and updates the mode indicator. */
  private afterFrame(viewer: HsHypersceneViewer): void {
    let picked: boolean = false;
    let hoverChanged: boolean = false;
    if (this.dragged === null && this.pointerMoved) {
      this.pointerMoved = false;
      picked = true;
      const pick: HsViewerPick | null = this.pointer === null ? null : viewer.pick(this.pointer[0], this.pointer[1]);
      hoverChanged = this.setHovered(viewer, pick === null ? null : this.getHighlightTarget(pick.object));
    }
    const renderType: RenderType = this.renderModesHandler.update({
      cameraMoved: this.cameraMoved,
      objectsMoved: this.objectsMoved,
      pointerMoved: picked,
      hoverChanged,
    });
    if (renderType !== this.renderType) {
      this.renderActions.set(this.renderType, false);
      this.renderActions.set(renderType, true);
      this.renderType = renderType;
      this.changeDetector.markForCheck();
    }
  }

  /** The picked object when hovering highlights it: tool handles with an action and the cubes. */
  private getHighlightTarget(object: HsGraphicObject): HsGraphicObject | null {
    if (object === this.ground) {
      return null;
    }
    for (const gizmo of this.gizmos) {
      const handle: GizmoHandle | null = gizmo.findHandle(object);
      if (handle !== null) {
        return handle.action === GizmoAction.None ? null : object;
      }
    }
    return object;
  }

  private setHovered(viewer: HsHypersceneViewer, object: HsGraphicObject | null): boolean {
    if (object === this.hovered || this.highlightMaterial === null) {
      return false;
    }
    if (this.hovered !== null && this.hoveredMaterial !== null) {
      this.hovered.material = this.hoveredMaterial;
    }
    this.hovered = object;
    this.hoveredMaterial = object?.material ?? null;
    if (object !== null) {
      object.material = this.highlightMaterial;
    }
    // A highlighted transparent handle turns opaque, which moves it to the other culling list.
    viewer.invalidate();
    return true;
  }

  private listen<K extends keyof DocumentEventMap & keyof HTMLElementEventMap>(
    target: HTMLElement | Document,
    type: K,
    listener: (event: DocumentEventMap[K]) => void
  ): void {
    target.addEventListener(type, listener as EventListener);
    this.listeners.push(() => target.removeEventListener(type, listener as EventListener));
  }
}
